import { useCallback, useEffect, useRef } from "react";
import type { Editor } from "tldraw";
import { importLessonSlides } from "../spacesCanvasUtils";

type UseLessonImportArgs = {
  spaceId: string | undefined;
  // Authoring teacher's user id — stamped into imported slide shapes' meta.
  userId: number | undefined;
  lessonIds: number[] | undefined;
  // True if the gateway already imported the lesson into the room snapshot —
  // skips the local import to avoid duplicate slide shapes.
  isAlreadyImported: boolean;
  getEditor: () => Editor | null;
  // Fired once after a successful import. Use it to reflect side effects in
  // the caller (e.g. mark draft as saved). Stable identity is not required —
  // the latest reference is captured per call.
  onImported?: () => void;
};

export type LessonImport = {
  // Call whenever any of the three preconditions might have just become true
  // (editor mount, sync reaching synced-remote, lessonIds arriving). Idempotent
  // — only the first call where all three line up does work; later calls
  // short-circuit on the import-done flag.
  tryImport: () => void;
  // Mark that the shared sync store reached `synced-remote`. tryImport waits
  // for this so slides are written into a server-acknowledged room.
  markSynced: () => void;
  // Reset the synced flag (e.g. after a generation bump on reconnect).
  resetSynced: () => void;
  // Force the import-done flag without running the import (used on reconnect
  // to prevent re-importing slides into a room that already has them).
  markImportDone: () => void;
};

/**
 * Owns the one-shot lesson-slide import for the teacher's whiteboard.
 * Three preconditions must all become true before slides land in the room:
 *   • spaceInfo loaded with lessonIds and !lessonImported
 *   • shared sync reached synced-remote (markSynced)
 *   • editor is mounted (getEditor returns non-null)
 *
 * tryImport is safe to call from any of those three transitions; it short-
 * circuits unless all are met. Once import is attempted the internal flag
 * flips and stays flipped — failures are terminal in this session to avoid a
 * race where a concurrent caller sees `done=true` between attempt and
 * rollback and silently skips. Surface failures to the user; refreshing the
 * page is the recovery path.
 */
export function useLessonImport({
  spaceId,
  userId,
  lessonIds,
  isAlreadyImported,
  getEditor,
  onImported,
}: UseLessonImportArgs): LessonImport {
  const importDoneRef = useRef(false);
  const syncedRef = useRef(false);
  // Captures the latest onImported without re-creating tryImport on every
  // render, so consumers don't need to memoize the callback.
  const onImportedRef = useRef(onImported);
  onImportedRef.current = onImported;

  const tryImport = useCallback(() => {
    if (importDoneRef.current) return;
    if (isAlreadyImported) return;
    if (!lessonIds || lessonIds.length === 0) return;
    if (!syncedRef.current) return;
    if (!spaceId || userId === undefined) return;
    const editor = getEditor();
    if (!editor) return;

    importDoneRef.current = true;
    importLessonSlides(editor, lessonIds, spaceId, userId)
      .then(() => onImportedRef.current?.())
      .catch((err) => {
        console.error("[useLessonImport] slide import failed:", err);
      });
  }, [spaceId, userId, lessonIds, isAlreadyImported, getEditor]);

  // If lessonIds (or any other precondition) arrives AFTER both editor and
  // sync are already ready, tryImport's deps will refresh and we re-run here.
  useEffect(() => {
    if (syncedRef.current && getEditor()) tryImport();
  }, [tryImport, getEditor]);

  const markSynced = useCallback(() => {
    syncedRef.current = true;
  }, []);
  const resetSynced = useCallback(() => {
    syncedRef.current = false;
  }, []);
  const markImportDone = useCallback(() => {
    importDoneRef.current = true;
  }, []);

  return { tryImport, markSynced, resetSynced, markImportDone };
}
